import type {
  TernaryOperatorNode as TernaryOperatorNodeType,
  Port,
  VariableValueType,
} from "../../../../util/src/blueprint/types.js";
import { OperatorNodeCard } from "./OperatorNodeCard.js";
import { PortHandle } from "../PortHandle.js";
import dico from "../../../../util/src/language/dico.js";
import React from "react";

export interface TernaryOperatorNodeProps {
  node: TernaryOperatorNodeType;
  setPortsMap: (
    nodeId: string,
    portId: string,
    element: HTMLElement | null,
  ) => void;
  onPortMouseDown?: (nodeId: string, port: Port) => void;
  onPortMouseUp?: (nodeId: string, port: Port) => void;
  isPortConnected?: (nodeId: string, portId: string) => boolean;
  onPortValueChange?: (nodeId: string, portId: string, value: unknown) => void;
  onListItemValueTypeChange?: (
    nodeId: string,
    valueType: Exclude<VariableValueType, "list">,
  ) => void;
}

/**
 * Renders a ternary operator node: 3 labelled input ports on the left,
 * the operator symbol in the centre, and 1 output port on the right.
 */
const TernaryOperatorNodeComponent = ({
  node,
  setPortsMap,
  onPortMouseDown,
  onPortMouseUp,
  isPortConnected,
  onPortValueChange,
  onListItemValueTypeChange,
}: TernaryOperatorNodeProps) => {
  const bpDico = dico().behavior;
  const inputPorts = node.ports.filter((p) => p.direction === "input");
  const outputPorts = node.ports.filter((p) => p.direction === "output");

  /** Per-port labels for known ternary operators. */
  const portLabel = (portId: string): string => {
    if (node.operator === "replace") {
      const labels = bpDico.operators.replacePorts;
      if (portId === "input1") return labels.source;
      if (portId === "input2") return labels.search;
      if (portId === "input3") return labels.replacement;
    }
    if (node.operator === "mix") {
      const labels = bpDico.operators.mixPorts;
      if (portId === "input1") return labels.colorA;
      if (portId === "input2") return labels.colorB;
      if (portId === "input3") return labels.ratio;
    }
    if (node.operator === "boolToSign") {
      const labels = bpDico.operators.boolToSignPorts;
      if (portId === "input1") return labels.condition;
      if (portId === "input2") return labels.whenTrue;
      if (portId === "input3") return labels.whenFalse;
    }
    if (node.operator === "ifElse") {
      const labels = bpDico.operators.ifElsePorts;
      if (portId === "input1") return labels.whenTrue;
      if (portId === "input2") return labels.whenFalse;
      if (portId === "input3") return labels.condition;
    }
    if (node.operator === "findByPath") {
      const labels = bpDico.operators.findByPathPorts;
      if (portId === "input1") return labels.list;
      if (portId === "input2") return labels.fieldPath;
      if (portId === "input3") return labels.expected;
    }
    return portId;
  };

  return (
    <OperatorNodeCard>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-evenly",
          height: "100%",
        }}
      >
        {inputPorts.map((port) => (
          <PortHandle
            setPortsMap={setPortsMap}
            node={node}
            key={port.id}
            port={{ ...port, label: portLabel(port.id) }}
            onMouseDown={() => onPortMouseDown?.(node.id, port)}
            onMouseUp={() => onPortMouseUp?.(node.id, port)}
            showValueInput={port.valueType !== "list"}
            isConnected={isPortConnected?.(node.id, port.id) ?? false}
            onValueChange={(value) =>
              onPortValueChange?.(node.id, port.id, value)
            }
            onListItemValueTypeChange={onListItemValueTypeChange}
          />
        ))}
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
          flexDirection: "column",
        }}
      >
        <span
          style={{
            fontSize: 22,
            fontWeight: 600,
            textTransform: "uppercase",
            color: "#333333",
            padding: "0 20px",
            whiteSpace: "nowrap",
          }}
        >
          {bpDico.operators[node.operator]}
        </span>
      </div>
      <div>
        {outputPorts.map((port) => (
          <PortHandle
            setPortsMap={setPortsMap}
            node={node}
            key={port.id}
            port={port}
            onMouseDown={() => onPortMouseDown?.(node.id, port)}
            onMouseUp={() => onPortMouseUp?.(node.id, port)}
            isConnected={isPortConnected?.(node.id, port.id) ?? false}
            onValueChange={(value) =>
              onPortValueChange?.(node.id, port.id, value)
            }
            onListItemValueTypeChange={onListItemValueTypeChange}
          />
        ))}
      </div>
    </OperatorNodeCard>
  );
};

export const TernaryOperatorNode = React.memo(TernaryOperatorNodeComponent);
